import Vue from 'vue'
import VueRouter from 'vue-router'
import _ from 'lodash'
import { Message } from 'element-ui'
import { ACCOUNT_TYPE } from '@/constant/common'
import { ROOT_COMPANY_ID } from '@/constant/company'
import store from '@/store'
import eventBus from '@/utils/eventBus'
import { deviceType } from '@/utils/common'
import { getHmToken } from '@/utils/hmToken'
import logger from '@/utils/logger'
import adminRoute from '@/views/admin/route'
import loginRoutes from '@/views/login/routes'
import errorRoute, { notFoundRouter } from '@/views/error/route'
import microSiteRoute from '@/views/microSite/route'
import onboardingRoute from '@/views/onboarding/route'
import surveyRoute from '@/views/survey/route'
// import starbucksRoute from '@/views/starbucks/route'
import jobDetailRoute from '@/views/job/route'
import jobListRoute from '@/views/company/route'
import crmRoute from '@/views/crm/route'
// import personalRoute from '@/views/personal/route'
import newPersonalRoute from '@/views/personalNew/route'
import personalAgreement from '@/views/personalAgreement/route'
import resourceDownloaderRoute from '@/views/resourceDownloader/route'
import questionnaireRoutes from '@/views/questionnaire/route'
import hmRoute from '@/views/hm/route'
import policyRoute from '@/views/policy/route'
import agencyRoute from '@/views/agency/route'
import newAdminRoute from '@/views/adminNew/route'
import consent from '@/views/personal/consentRoute'

Vue.use(VueRouter)

const routes = [
  {
    path: '/',
    redirect: '/mobile-web-more-jobs/64',
    meta: {
      noAuth: true
    }
  },
  // { path: '/mobile-web-more-jobs/3738/', redirect: '/starbucks_external_wechat' },
  ...jobListRoute,
  policyRoute,
  jobDetailRoute,
  // starbucksRoute,
  adminRoute,
  newAdminRoute,
  ...loginRoutes,
  onboardingRoute,
  microSiteRoute,
  surveyRoute,
  crmRoute,
  ...newPersonalRoute,
  personalAgreement,
  resourceDownloaderRoute,
  questionnaireRoutes,
  hmRoute,
  agencyRoute,
  consent,
  errorRoute // this router must be at bottom
]

const hasOldPageRouteList = [adminRoute, newAdminRoute, newPersonalRoute, agencyRoute]

const hrRoute = [
  adminRoute,
  microSiteRoute,
  hmRoute,
  agencyRoute,
  newAdminRoute
]

let companyId = null

function formatRoutes (routes, isNoAuth) {
  _.forEach(routes, item => {
    const meta = item.meta
    if (isNoAuth) {
      if (!meta || !meta.hasOwnProperty('noAuth')) {
        item.meta = {
          ...meta,
          noAuth: true
        }
      }
    }
    if (item.children && meta && meta.noAuth) {
      formatRoutes(item.children, true)
    }
  })
  return routes
}

function injectionParent (routes, parent) {
  _.forEach(routes, item => {
    if (parent) {
      let meta = item.meta
      if (!meta) {
        meta = {}
        item.meta = meta
      }
      meta._parent = parent
    }
    if (item.children) {
      injectionParent(item.children, item)
    }
  })
}

function resetNewAdminLayoutScrollbar (to, from) {
  const parentMates = []
  let meta = to.meta
  while (meta) {
    parentMates.push(meta)
    meta = meta._parent?.meta
  }
  if (parentMates.includes(newAdminRoute.meta)) {
    const contentEl = document.querySelector('.root-layout .main-content')
    if (contentEl) {
      contentEl.scrollTo({ top: 0, left: 0 })
    } else if (process.env.NODE_ENV !== 'production') {
      Message.error('重置滚动条失败，请检查HTML代码')
    }
  }
}

const createRouter = () => {
  const formattedRoutes = formatRoutes(routes)
  injectionParent(formattedRoutes)
  return new VueRouter({
    mode: 'history', // require service support
    scrollBehavior: (to, from, savedPosition) => {
      if (to.hash) {
        return false
      }
      if (noScrollBehaviorPath.includes(to.path) && noScrollBehaviorPath.includes(from.path)) {
        return false
      }
      for (let i = 0; i < noScrollBehaviorPathPrefix.length; i++) {
        const prefix = noScrollBehaviorPathPrefix[i]
        if (to.path.startsWith(prefix) && from.path.startsWith(prefix)) {
          return false
        }
      }
      if (savedPosition) {
        if (savedPosition.x === 0 && savedPosition.y === 0) {
          resetNewAdminLayoutScrollbar(to, from)
        }
        return savedPosition
      } else {
        resetNewAdminLayoutScrollbar(to, from)
        return { x: 0, y: 0 }
      }
    },
    routes: formattedRoutes
  })
}

const router = createRouter()

const noAuthPath = [
  '/'
]

const noScrollBehaviorPath = []

const noScrollBehaviorPathPrefix = [
  '/mobile-web-more-jobs/',
  '/recruiting/company-detail/'
]

function isNoAuthPath (to) {
  const meta = to.meta
  if (meta && meta.noAuth) {
    return true
  }
  if (noAuthPath.includes(to.path)) {
    return true
  }
  return false
}

function getHmRootCompanyResolveParams (to) {
  let recordType = null
  let inviteCode = null
  if (to.path.startsWith('/v-admin/hm/rating/')) {
    recordType = 'screening'
    inviteCode = to.params.inviteCode
  } else if (to.path.startsWith('/v-admin/hm/interview/')) {
    recordType = 'interview'
    inviteCode = to.params.inviteCode
  } else if (to.path === '/v-admin/hm/interview-invitation') {
    recordType = 'interview'
  }
  if (!recordType || (!to.query.uuid && !inviteCode)) {
    return null
  }
  return _.pickBy({
    record_type: recordType,
    uuid: to.query.uuid,
    invite_code: inviteCode
  })
}

async function ensureHmRootCompany (to) {
  if (!to.path.startsWith('/v-admin/hm/') || !getHmToken()) {
    return true
  }

  let targetRootCompanyId = to.query.company_id
  if (!targetRootCompanyId) {
    const resolveParams = getHmRootCompanyResolveParams(to)
    if (resolveParams) {
      const resolvedContext = await store.dispatch('hm/resolveRootCompany', resolveParams)
      targetRootCompanyId = resolvedContext.root_company_id
    }
  }
  if (!targetRootCompanyId) {
    return true
  }

  targetRootCompanyId = Number(targetRootCompanyId)
  if (!Number.isInteger(targetRootCompanyId) || targetRootCompanyId <= 0) {
    return false
  }

  const hmInfo = await store.dispatch('hm/getHmInfo')
  if (targetRootCompanyId === hmInfo.current_root_company_id) {
    return true
  }

  const hasTargetRootCompany = _.some(hmInfo.root_company_list, { id: targetRootCompanyId })
  if (!hasTargetRootCompany) {
    return false
  }

  await store.dispatch('hm/switchRootCompany', targetRootCompanyId)
  const switchedHmInfo = await store.dispatch('hm/getHmInfo')
  return switchedHmInfo.current_root_company_id === targetRootCompanyId
}

function getWxLoginParams (to) {
  if (!deviceType.isWeChat) {
    return
  }
  const path = to.path
  const params = {
    [deviceType.isWeChatWork ? 'wecom_oauth' : 'wx_oauth']: 1,
    wx_login_key: null,
    wx_login_val: null,
    origin_from: to.fullPath
  }
  if (path.startsWith('/job-description/')) {
    params.wx_login_key = 'job_short_id'
    params.wx_login_val = to.params.jobId
  } else if (path.startsWith('/recruiting/crm/campaign')) {
    params.wx_login_key = 'campaign_short_id'
    params.wx_login_val = to.params.siteId
  } else if (path.startsWith('/recruiting/company/')) {
    if (!path.includes('/login') && !path.includes('/register/') && !path.includes('/find-password/')) {
      params.wx_login_key = 'company_id'
      params.wx_login_val = to.params.companyId
    }
  } else if (path.startsWith('/mobile-web-more-jobs')) {
    params.wx_login_key = 'company_id'
    params.wx_login_val = to.params.companyId
  } else if (path.startsWith('/recruiting/personal') && to.query.company_id) {
    params.wx_login_key = 'company_id'
    params.wx_login_val = to.query.company_id
  } else if (path.startsWith('/microsite/crm')) {
    params.wx_login_key = 'microsite_short_id'
    params.wx_login_val = to.params.siteId
  } else if (path.startsWith('/passport') && to.query.company_id && (path.includes('/login') || path.includes('/register'))) {
    params.wx_login_key = 'company_id'
    params.wx_login_val = to.query.company_id
  } else if (path.startsWith('/msdchina')) {
    const companyId = to.meta?.customCompanyId || ROOT_COMPANY_ID.MSD
    params.wx_login_key = 'company_id'
    params.wx_login_val = companyId
  }
  if (params.wx_login_key) {
    return params
  }
}

// set current referral to cookie if referral id param exists in url
function handleReferral (to) {
  const referralStartFlag = window.sessionStorage.getItem('referral_start_flag')
  if (!referralStartFlag) {
    window.document.cookie = 'current_referral_id=;path=/;expires=Thu, 01 Jan 1970 00:00:00 UTC;'
  }
  window.sessionStorage.setItem('referral_start_flag', true)
  if (to.query.referId) {
    window.document.cookie = `current_referral_id=${to.query.referId};path=/;expires=Session`
  }
}

// 为弹性布局页面添加统一的前缀属性
function checkUseFlexible (route) {
  if (deviceType.isPc) {
    delete document.body.dataset.mobileFlexible
    return
  }
  let isAllFlexible = false
  let meta = route.meta
  if (!meta) {
    delete document.body.dataset.mobileFlexible
    return
  }
  while (meta) {
    if (meta.mobileAllFlexible) {
      isAllFlexible = true
      break
    }
    meta = meta._parent?.meta
  }
  if (isAllFlexible) {
    document.body.dataset.mobileFlexible = true
  } else if (route.meta.mobileFlexible) {
    document.body.dataset.mobileFlexible = true
  } else {
    delete document.body.dataset.mobileFlexible
  }
}

function hasCustomDomain () {
  const domain = window.location.hostname
  let pattern = /^.*\.ajinga\.com$/
  if (process.env.NODE_ENV !== 'production') {
    pattern = /^(.*\.ajinga\.com)|(localhost|127.0.0.1)$/
  }
  return !pattern.test(domain)
}

function getCompanyIdByRoute (route) {
  return route.query.company_id || route.meta?.customCompanyId || route.params.companyId
}

function getCompanyIdFromBack (route) {
  const back = route.query.back
  if (!back) {
    return null
  }
  try {
    return new URL(back, window.location.origin).searchParams.get('company_id')
  } catch {
    return null
  }
}

function handleCustomDomainRouting (to, next, query) {
  const companyId = getCompanyIdByRoute(to)
  const domainCompany = store.getters['company/domainCompany']
  if (hasCustomDomain() && !companyId && domainCompany.id) {
    next({ path: to.path, query: query, replace: true })
  } else {
    next()
  }
}

async function ensureCompanyTheme (to, query) {
  let currentCompanyId = query.company_id || getCompanyIdByRoute(to)

  let rootRoute = to
  while (rootRoute?.meta._parent) {
    rootRoute = rootRoute.meta._parent
  }
  const isHrRootRoute = _.includes(hrRoute, rootRoute)

  const account = store.getters['account/getCurrentAccount']
  if (!currentCompanyId && isHrRootRoute) {
    currentCompanyId = account?.company_id
  }

  currentCompanyId = Number(currentCompanyId)
  if (!currentCompanyId) {
    if (isHrRootRoute) {
      return
    }
    companyId = null
    store.dispatch('company/clearCompanyThemeSetting')
    return
  }

  if (store.getters['company/companyThemeSetting'] && currentCompanyId === companyId) {
    return
  }

  companyId = currentCompanyId
  await store.dispatch('company/getCompanyTheme', { companyId: currentCompanyId })
}

let dataInitFinished = false

router.beforeEach(async (to, from, next) => {
  handleReferral(to)
  if (to.name === '404Error') {
    logger.send({
      type: 'vue.router.beforeEach',
      from: 'args',
      data: {
        to: {
          ...to,
          matched: to.matched.length,
          meta: {
            ...to.meta,
            _parent: undefined
          }
        },
        from: {
          ...from,
          matched: from.matched.length,
          meta: {
            ...from.meta,
            _parent: undefined
          }
        }
      }
    })
  }
  const rootCompanyReady = await ensureHmRootCompany(to)
  if (!rootCompanyReady) {
    next('/error/no-permission')
    return
  }
  let query = to.query
  const backCompanyId = getCompanyIdFromBack(to)
  if (!query.company_id && backCompanyId) {
    query = {
      ...query,
      company_id: backCompanyId
    }
  }
  if (hasCustomDomain()) {
    let valid = true
    const companyId = getCompanyIdByRoute(to)
    const domainCompany = await store.dispatch('company/getDomainCompany')
    if (!domainCompany || !domainCompany.id) {
      valid = true
    }
    const domainCompanyIdList = domainCompany.children
    if (companyId && !domainCompanyIdList.includes(Number(companyId))) {
      valid = false
    }
    if (valid) {
      const domainCompany = store.getters['company/domainCompany']
      if (domainCompany.id) {
        query = {
          ...query,
          company_id: domainCompany.id
        }
      }
    } else {
      next('/error/404.html')
      return
    }
  } else {
    store.dispatch('company/clearDomainCompany')
  }

  checkUseFlexible(to)
  const wxLoginParams = getWxLoginParams(to)
  if (!dataInitFinished) {
    dataInitFinished = true
    eventBus.nativeShowLoading = true
    setTimeout(() => {
      if (eventBus.nativeShowLoading) {
        eventBus.nativeShowLoading = false
      }
    }, 90000) // 90000 is timeout option item about axios request
    const wxOauthUrl = await store.dispatch('account/getSessionAccountInfoWithWxAutoLogin', wxLoginParams)
    if (deviceType.isWeChat) {
      const weChatQrCodeLoginNeedRefresh = to.query.is_refresh === '1'
      const account = store.getters['account/getCurrentAccount']
      if (account && account.id && weChatQrCodeLoginNeedRefresh) {
        const newQuery = _.omit(to.query, 'is_refresh')
        let newUrl = window.location.origin + to.path
        if (_.size(newQuery) > 0) {
          newUrl = `${newUrl}?${_.map(newQuery, (value, key) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`).join('&')}`
        }
        window.location.href = newUrl
        return
      }
    }
    if (wxOauthUrl) {
      window.location.href = wxOauthUrl
      return
    }
    eventBus.nativeShowLoading = false
  } else if (wxLoginParams) {
    const wxOauthUrl = await store.dispatch('account/getSessionAccountInfoWithWxAutoLogin', wxLoginParams)
    if (wxOauthUrl) {
      window.location.href = wxOauthUrl
      return
    }
  }

  await ensureCompanyTheme(to, query)

  if (isNoAuthPath(to)) {
    if (!to.query.company_id && query.company_id) {
      next({ path: to.path, query: query, hash: to.hash, replace: true })
      return
    }
    // 如果登陆页参数携带account_id并且与该设备已经登陆的账号不匹配，则退出登陆至登陆页
    if (to.path === '/passport/recruiter/login/' && to.query.account_id) {
      const account = store.getters['account/getCurrentAccount']
      const queryAccountId = to.query.account_id
      if (account.id !== parseInt(queryAccountId)) {
        window.location.href = `/logout/?backurl=${encodeURIComponent(`/passport/${ACCOUNT_TYPE.HR}/login`)}`
      }
    }
    handleCustomDomainRouting(to, next, query)
  } else {
    const account = store.getters['account/getCurrentAccount']
    if (account && account.id) {
      if (hasCustomDomain() && (store.getters['account/isCompanyAccount'] || store.getters['account/isHeadhunterAccount'])) {
        window.location.href = process.env.VUE_APP_STYLE_BASE_URL
        return
      }
      const hasAnyPermissions = store.getters['account/hasAnyPermission']
      if (hasAnyPermissions(to.meta.permission)) {
        if (store.getters['account/hasEmployeeAccountInfo'] && hasAnyPermissions(to.meta.employeePermission)) {
          handleCustomDomainRouting(to, next, query)
        } else {
          handleCustomDomainRouting(to, next, query)
        }
      } else {
        next('/error/no-permission')
      }
    } else if (to.path.startsWith('/onboarding/')) {
      const invitationId = to.params.invitationId
      if (invitationId) {
        next({ path: `/passport/${ACCOUNT_TYPE.CANDIDATE}/register`, params: { onboarding_id: invitationId, back: to.fullPath }, query: query })
      } else {
        next('/error/404.html')
      }
      // } else if (to.path.startsWith('/recruiting/company/')) {
      //   const companyId = to.params.companyId
      //   if (companyId) {
      //     next({ path: '/passport/candidate/login', params: { company_id: companyId, back: to.fullPath } })
      //   } else {
      //     next('/error/404.html')
      //   }
    } else {
      let accountType = ACCOUNT_TYPE.CANDIDATE
      if (to.path.startsWith('/v-admin/hm/')) {
        accountType = ACCOUNT_TYPE.HIRING_MANAGER
      } else if (to.path.startsWith('/v-admin')) {
        accountType = ACCOUNT_TYPE.HR
      }
      next({ path: `/passport/${accountType}/${accountType === ACCOUNT_TYPE.CANDIDATE ? 'register' : 'login'}/`, query: { ...query, back: to.fullPath } })
    }
  }
})

export default router

// Detail see: https://github.com/vuejs/vue-router/issues/1234#issuecomment-357941465
export function resetRouter () {
  const newRouter = createRouter()
  router.matcher = newRouter.matcher // reset router
}

export function getRouteParentList (route) {
  const parentList = []
  let parent = route
  while ((parent = parent.meta?._parent)) {
    parentList.push(parent)
  }
  return parentList
}

export function hasOldPageRoute (route) {
  const parentList = getRouteParentList(route)
  return _.some(hasOldPageRouteList, (route) => parentList.includes(route))
}

export function getIncludeRoutePath (to) {
  if (typeof to !== 'string') {
    return to
  }
  if (to.startsWith('http')) {
    const origin = window.location.origin
    if (to.startsWith(origin)) {
      to = to.substring(origin.length)
    } else {
      return null
    }
  }
  if (to === notFoundRouter.path) {
    return to
  }
  const result = router.resolve(to)
  const resolved = result.resolved
  const resolvedName = resolved.name
  if (resolvedName === notFoundRouter.name || resolvedName === null) {
    return null
  } else {
    if (hasOldPageRoute(resolved)) {
      let isAllInclude = false
      let meta = resolved.meta
      while (meta) {
        if (meta.isAllInclude) {
          isAllInclude = true
          break
        }
        meta = meta._parent?.meta
      }
      if (isAllInclude) {
        return to
      }
      return resolved.meta?.isInclude ? to : null
    } else {
      return resolved.meta?.isInclude === false ? null : to
    }
  }
}

export function mixedRouterPush (link) {
  const path = getIncludeRoutePath(link)
  if (path) {
    return router.push(path)
  } else {
    window.location.href = link
    return Promise.resolve(link)
  }
}
